/**
 * E-mail intake outcome contract (ALC-4467, epic ALC-4465 "e-mail intake you can trust").
 *
 * Every accepted e-mail ends with ONE recorded final outcome plus a stable reason code, stored on
 * `EmailMessage.intakeOutcome` / `intakeReasonCode` / `intakeOutcomeDetails`. The reply to the
 * sender (S2) and the finance "E-mails que não viraram conta" alert (S5) read only these values —
 * never message text, never `EmailMessage.status` (PROCESSED only means "the worker finished").
 *
 * The values mirror the Prisma enum `EmailIntakeOutcome`; keep both lists identical. The enum
 * itself (value object + type) is generated into `./generated/enums.ts` and exported from the
 * package index, so this file must not declare a second `EmailIntakeOutcome`: two `export *`
 * with the same name is TS2308 the moment `enums.ts` is regenerated.
 */

/** Final outcome of one e-mail, as a tuple. Mirrors the Prisma enum `EmailIntakeOutcome`. */
export const EMAIL_INTAKE_OUTCOMES = [
  /** At least one attachment (or the body) made a NEW bill. */
  'BILL_CREATED',
  /** At least one attachment opened a reimbursement request and none made a bill. */
  'REIMBURSEMENT_CREATED',
  /** Nothing was billed. `intakeReasonCode` says why; the S5 alert lists these. */
  'NO_BILL',
  /** The e-mail is waiting on the sender (missing document, PDF password, unclear objective). */
  'AWAITING_INFO',
  /** Rejected by policy (spam). No reply, no alert. */
  'REJECTED',
] as const;

/**
 * Stable reason codes. Used both for the whole e-mail (`EmailMessage.intakeReasonCode`) and per
 * attachment (`intakeOutcomeDetails.attachments[].reasonCode`). Classify by these codes only.
 */
export const EMAIL_INTAKE_REASON_CODES = {
  /** A new bill was made from this document / e-mail. */
  BILL_CREATED: 'EMAIL_INTAKE_BILL_CREATED',
  /** The document was routed to a reimbursement request (no payable PDF, e-mail asked for it). */
  REIMBURSEMENT_CREATED: 'EMAIL_INTAKE_REIMBURSEMENT_CREATED',
  /** The same document / NF already has a bill; `existingBillId` names it. */
  DUPLICATE_DOCUMENT: 'EMAIL_INTAKE_DUPLICATE_DOCUMENT',
  /** Read with confidence, but it is not a payable document (comprovante, tax guide, signature). */
  NOT_PAYABLE_DOCUMENT: 'EMAIL_INTAKE_NOT_PAYABLE_DOCUMENT',
  /** The document could not be read or was classified below the payable threshold. */
  LOW_CONFIDENCE: 'EMAIL_INTAKE_LOW_CONFIDENCE',
  /** The document was read but the vendor CNPJ/CPF could not be found, so no bill could be made. */
  VENDOR_TAX_ID_UNREADABLE: 'EMAIL_INTAKE_VENDOR_TAX_ID_UNREADABLE',
  /**
   * The document was read (vendor tax id present) but the vendor or the company could not be
   * resolved automatically, so the bill must be made by hand (`MANUAL_BILL_CREATION_REQUIRED`).
   */
  MANUAL_BILL_REQUIRED: 'EMAIL_INTAKE_MANUAL_BILL_REQUIRED',
  /** The document was attached to a bill that already existed (`existingBillId`); no new bill. */
  LINKED_TO_EXISTING_BILL: 'EMAIL_INTAKE_LINKED_TO_EXISTING_BILL',
  /** A technical error stopped processing. */
  PROCESSING_ERROR: 'EMAIL_INTAKE_PROCESSING_ERROR',
  /** The e-mail had no usable attachment and the body was not enough to make a bill. */
  NO_ATTACHMENT: 'EMAIL_INTAKE_NO_ATTACHMENT',
  /** A PDF is password protected; the sender was asked for the password. */
  PASSWORD_REQUIRED: 'EMAIL_INTAKE_PASSWORD_REQUIRED',
  /** The sender was asked for missing data or for the e-mail's objective. */
  AWAITING_INFO: 'EMAIL_INTAKE_AWAITING_INFO',
  /** Classified as spam and rejected. */
  SPAM: 'EMAIL_INTAKE_SPAM',
  /**
   * Integration-channel (Pipefy) e-mail that made no bill and has no more specific reason.
   * Integration e-mails ALSO carry `intakeReplySuppressedReason = 'INTEGRATION_CHANNEL'`.
   */
  INTEGRATION_CHANNEL: 'EMAIL_INTAKE_INTEGRATION_CHANNEL',
  /**
   * The payable document was issued BY one of the tenant's own companies (the vendor/emitente tax id
   * is a tenant entity tax id): a sale or a copy of our own NF, not a bill to pay. No bill is made.
   */
  SELF_ISSUED_DOCUMENT: 'EMAIL_INTAKE_SELF_ISSUED_DOCUMENT',
  /**
   * A retry ("Tentar novamente") needed something only the sender can give, but the e-mail's one
   * final reply already went out (L2), so nobody was asked: the PDF password.
   */
  NEEDS_SENDER_PASSWORD: 'EMAIL_INTAKE_NEEDS_SENDER_PASSWORD',
  /** As NEEDS_SENDER_PASSWORD, for the vendor CNPJ/CPF missing from the e-mail body. */
  NEEDS_SENDER_TAX_ID: 'EMAIL_INTAKE_NEEDS_SENDER_TAX_ID',
  /** As NEEDS_SENDER_PASSWORD, for the document itself or other missing data (objective, values). */
  NEEDS_SENDER_INFO: 'EMAIL_INTAKE_NEEDS_SENDER_INFO',
  /**
   * The inbox serves several companies and nothing in the e-mail said which one it is for (no
   * tax id, no sure AI pick, no answer from the sender). A person chooses the company on the
   * no-bill card, and the e-mail is processed again for it.
   */
  ENTITY_UNRESOLVED: 'EMAIL_INTAKE_ENTITY_UNRESOLVED',
} as const;

/**
 * An inbox that serves two or more companies: an e-mail that names none of them waits for a
 * choice (AI when sure, else the sender or finance). One company, or none listed, routes as before.
 */
export function isSharedInbox(inboxEntityIds: readonly string[]): boolean {
  return inboxEntityIds.length >= 2;
}

/** `ApDocument.metadata` key: the document waits for someone to choose its company. */
export const AWAITING_ENTITY_CHOICE_METADATA_KEY = 'awaitingEntityChoice';

export type EmailIntakeReasonCode =
  (typeof EMAIL_INTAKE_REASON_CODES)[keyof typeof EMAIL_INTAKE_REASON_CODES];

export const EMAIL_INTAKE_REASON_CODE_VALUES = Object.values(
  EMAIL_INTAKE_REASON_CODES,
) as readonly EmailIntakeReasonCode[];

export function isEmailIntakeReasonCode(value: unknown): value is EmailIntakeReasonCode {
  return (
    typeof value === 'string' &&
    (EMAIL_INTAKE_REASON_CODE_VALUES as readonly string[]).includes(value)
  );
}

/** Why no reply goes to the sender. Stored on `EmailMessage.intakeReplySuppressedReason`. */
export const EMAIL_INTAKE_REPLY_SUPPRESSED_REASONS = {
  /** Integration channel (Pipefy): feedback goes back to the card, never by e-mail (Q15/Q16). */
  INTEGRATION_CHANNEL: 'INTEGRATION_CHANNEL',
} as const;

export type EmailIntakeReplySuppressedReason =
  (typeof EMAIL_INTAKE_REPLY_SUPPRESSED_REASONS)[keyof typeof EMAIL_INTAKE_REPLY_SUPPRESSED_REASONS];

/** One attachment's result, inside `EmailMessage.intakeOutcomeDetails.attachments`. */
export interface EmailIntakeAttachmentOutcome {
  /** Index in `EmailMessage.attachments` when known; null for the body or unmatched documents. */
  attachmentIndex: number | null;
  filename: string;
  documentId: string | null;
  /** Pre-classifier label (`DocumentTypeClassifierLabel`) when known. */
  classifiedType: string | null;
  /** Pre-classifier confidence, 0..1. */
  classifiedConfidence: number | null;
  /** True when the label is payable AND the confidence reached the payable threshold. */
  payable: boolean;
  reasonCode: EmailIntakeReasonCode;
  /** Bill made from this attachment (reason BILL_CREATED). */
  billId: string | null;
  /** Bill that already existed for this document / NF (reason DUPLICATE_DOCUMENT). */
  existingBillId: string | null;
  /** Reimbursement request opened from this attachment (reason REIMBURSEMENT_CREATED). */
  reimbursementRequestId: string | null;
}

/**
 * The e-mail classifier's own answer before the payable-document rule overrode it to AP (Q1).
 * Kept so a person can see that, e.g., the agent said SPAM but a payable NF made a bill anyway.
 */
export interface EmailIntakeRawObjective {
  /** Raw `EmailClassification.objective` (AP, AR, REIMBURSEMENT, SPAM, UNKNOWN, ...). */
  objective: string;
  /** Raw classifier confidence, 0..1, or null when absent. */
  confidence: number | null;
}

/** Shape of `EmailMessage.intakeOutcomeDetails` (JSON). */
export interface EmailIntakeOutcomeDetails {
  version: 1;
  /** Which code path recorded the outcome (diagnostics only; never branch on it). */
  recordedBy: string;
  /** True when the payable-document rule (Q1/Q6) routed this e-mail to AP. */
  payableRuleApplied: boolean;
  /**
   * Raw e-mail objective + confidence when the payable rule overrode it (`payableRuleApplied`).
   * Absent / null otherwise and on outcomes recorded before ALC-4467 fix round 1.
   */
  emailObjective?: EmailIntakeRawObjective | null;
  attachments: EmailIntakeAttachmentOutcome[];
}

/**
 * "Conferir" alert raised on a bill the payable rule made from an e-mail the classifier called SPAM
 * (D5). Stored as an OPEN `ApBillAlert` `INTAKE_METADATA_MISMATCH` with `message = code` and
 * `metadata.source = EMAIL_SPAM_OVERRIDE_ALERT_SOURCE`; auto-submit refuses the bill while it is
 * open, so a person confirms the sender before anything is approved or paid.
 */
export const EMAIL_SPAM_OVERRIDE_ALERT_SOURCE = 'EMAIL_SPAM_OVERRIDE' as const;
export const EMAIL_SPAM_OVERRIDE_ALERT_CODE = 'EMAIL_SPAM_OVERRIDE_NEEDS_REVIEW' as const;
export type EmailSpamOverrideAlertCode = typeof EMAIL_SPAM_OVERRIDE_ALERT_CODE;

export const EMAIL_SPAM_OVERRIDE_ALERT_MESSAGES: Record<
  EmailSpamOverrideAlertCode,
  { ptBr: string; en: string }
> = {
  EMAIL_SPAM_OVERRIDE_NEEDS_REVIEW: {
    ptBr: 'Conferir: o e-mail foi classificado como spam, mas trazia uma nota a pagar e a conta foi criada. Confirme o remetente e o documento antes de enviar para aprovação.',
    en: 'Check: the e-mail was classified as spam, but it carried a payable document and the bill was created. Confirm the sender and the document before sending for approval.',
  },
};

/** One piece of copy: what happened + what to do next. */
export interface EmailIntakeReasonCopy {
  message: string;
  nextStep: string;
}

/**
 * pt-BR copy per reason code (Q3/Q11: plain words + what to do next), split by audience (D4):
 * - `sender`: the reply to whoever sent the e-mail (S2). Often a vendor — it never asks them to
 *   post a bill, and only promises what the system does.
 * - `finance`: the "E-mails que não viraram conta" list (S5). Its English is
 *   `EMAIL_INTAKE_REASON_COPY_EN` below.
 * Resolve text from the code only, never from message text.
 */
export const EMAIL_INTAKE_REASON_COPY_PT_BR: Record<
  EmailIntakeReasonCode,
  { sender: EmailIntakeReasonCopy; finance: EmailIntakeReasonCopy }
> = {
  EMAIL_INTAKE_BILL_CREATED: {
    sender: {
      message: 'Recebemos o documento e a conta foi criada.',
      nextStep: 'Nenhuma ação necessária.',
    },
    finance: {
      message: 'Conta criada a partir do documento.',
      nextStep: 'Nenhuma ação necessária.',
    },
  },
  EMAIL_INTAKE_REIMBURSEMENT_CREATED: {
    sender: {
      message: 'Recebemos o documento e abrimos um pedido de reembolso.',
      nextStep: 'Nenhuma ação necessária por enquanto.',
    },
    finance: {
      message: 'Pedido de reembolso aberto a partir do documento.',
      nextStep: 'Acompanhe a aprovação do reembolso no sistema.',
    },
  },
  EMAIL_INTAKE_DUPLICATE_DOCUMENT: {
    sender: {
      message: 'Esta nota já tinha sido enviada antes e já está registrada.',
      nextStep: 'Nenhuma ação necessária. Se for uma nota nova, confira o número e envie de novo.',
    },
    finance: {
      message: 'Esta nota já foi enviada antes e já tem uma conta.',
      nextStep: 'Nenhuma ação necessária; a conta existente está indicada.',
    },
  },
  EMAIL_INTAKE_NOT_PAYABLE_DOCUMENT: {
    sender: {
      message: 'O arquivo não parece uma nota fiscal, boleto ou fatura.',
      nextStep: 'Se há algo a pagar, envie a nota fiscal, o boleto ou a fatura.',
    },
    finance: {
      message:
        'O arquivo foi lido como um documento que não se paga (comprovante, guia, assinatura).',
      nextStep: 'Confira o arquivo; se for a pagar, lance a conta manualmente.',
    },
  },
  EMAIL_INTAKE_LOW_CONFIDENCE: {
    sender: {
      message: 'Não conseguimos ler o arquivo com segurança.',
      nextStep: 'Envie o arquivo em PDF legível (não escaneado nem foto borrada).',
    },
    finance: {
      message: 'O arquivo não pôde ser lido com segurança.',
      nextStep: 'Abra o arquivo e, se for a pagar, lance a conta manualmente.',
    },
  },
  EMAIL_INTAKE_VENDOR_TAX_ID_UNREADABLE: {
    sender: {
      message: 'Não conseguimos ler o CNPJ do emitente na nota.',
      nextStep: 'Envie a nota fiscal de novo, em um arquivo em que o CNPJ esteja legível.',
    },
    finance: {
      message: 'O CNPJ/CPF do fornecedor não foi encontrado no documento.',
      nextStep: 'Confira o documento e lance a conta manualmente, ou aguarde o reenvio.',
    },
  },
  EMAIL_INTAKE_MANUAL_BILL_REQUIRED: {
    sender: {
      message: 'Recebemos o documento; a equipe financeira vai concluir o registro.',
      nextStep: 'Nenhuma ação necessária.',
    },
    finance: {
      message:
        'O documento foi lido, mas o fornecedor ou a empresa não foram identificados automaticamente.',
      nextStep: 'Lance a conta manualmente a partir do documento no sistema.',
    },
  },
  EMAIL_INTAKE_LINKED_TO_EXISTING_BILL: {
    sender: {
      message: 'O documento foi anexado a uma conta que já existia.',
      nextStep: 'Nenhuma ação necessária.',
    },
    finance: {
      message: 'O documento foi anexado a uma conta que já existia.',
      nextStep: 'Nenhuma ação necessária; a conta existente está indicada.',
    },
  },
  EMAIL_INTAKE_PROCESSING_ERROR: {
    sender: {
      message: 'Tivemos um problema ao processar este e-mail.',
      nextStep: 'A equipe financeira vai ver este e-mail.',
    },
    finance: {
      message: 'Ocorreu um erro técnico ao processar o e-mail.',
      nextStep: 'Abra os anexos e, se houver algo a pagar, lance a conta manualmente.',
    },
  },
  EMAIL_INTAKE_NO_ATTACHMENT: {
    sender: {
      message: 'O e-mail não tem um documento que possamos usar para criar a conta.',
      nextStep: 'Responda este e-mail com a nota fiscal, o boleto ou a fatura em anexo.',
    },
    finance: {
      message: 'O e-mail não tem anexo utilizável e o texto não bastou para criar a conta.',
      nextStep: 'Confira o e-mail; se houver algo a pagar, peça o documento ao remetente.',
    },
  },
  EMAIL_INTAKE_PASSWORD_REQUIRED: {
    sender: {
      message: 'Um dos PDFs está protegido por senha.',
      nextStep: 'Responda este e-mail com a senha do PDF.',
    },
    finance: {
      message: 'Um PDF está protegido por senha; a senha foi pedida ao remetente.',
      nextStep: 'Aguarde a resposta do remetente.',
    },
  },
  EMAIL_INTAKE_AWAITING_INFO: {
    sender: {
      message: 'Precisamos de mais informações para continuar.',
      nextStep: 'Responda este e-mail com as informações pedidas.',
    },
    finance: {
      message: 'Faltam informações; elas foram pedidas ao remetente.',
      nextStep: 'Aguarde a resposta do remetente.',
    },
  },
  EMAIL_INTAKE_SPAM: {
    sender: {
      message: 'E-mail classificado como spam.',
      nextStep: 'Nenhuma ação necessária.',
    },
    finance: {
      message: 'E-mail classificado como spam e descartado.',
      nextStep: 'Nenhuma ação necessária.',
    },
  },
  EMAIL_INTAKE_INTEGRATION_CHANNEL: {
    sender: {
      message: 'A solicitação recebida pela integração não gerou conta.',
      nextStep: 'Confira o card na integração.',
    },
    finance: {
      message: 'A solicitação recebida pela integração (Pipefy) não gerou conta.',
      nextStep: 'Confira o card na integração ou lance a conta manualmente.',
    },
  },
  EMAIL_INTAKE_SELF_ISSUED_DOCUMENT: {
    sender: {
      message: 'A nota foi emitida pela própria empresa, então não é uma conta a pagar.',
      nextStep: 'Se houver algo a pagar, envie a nota emitida pelo fornecedor.',
    },
    finance: {
      message:
        'A nota foi emitida por uma das empresas do grupo (o CNPJ do emitente é nosso); nenhuma conta foi criada.',
      nextStep:
        'Se for uma venda, registre-a em contas a receber; se for mesmo a pagar, lance a conta manualmente.',
    },
  },
  // The three NEEDS_SENDER_* codes are recorded only when the sender cannot be asked (the final reply
  // was already sent, or the inbox has auto-reply off), so the sender copy is never mailed today; it
  // exists so every code can be answered if that changes.
  EMAIL_INTAKE_NEEDS_SENDER_PASSWORD: {
    sender: {
      message: 'Um dos PDFs está protegido por senha.',
      nextStep: 'Envie o PDF sem senha ou informe a senha à equipe financeira.',
    },
    finance: {
      message:
        'Um dos PDFs está protegido por senha. O remetente não foi consultado: a resposta já tinha sido enviada ou a caixa de entrada não responde automaticamente.',
      nextStep: 'Peça a senha ao fornecedor e envie a conta manualmente.',
    },
  },
  EMAIL_INTAKE_NEEDS_SENDER_TAX_ID: {
    sender: {
      message: 'Não encontramos o identificador fiscal do fornecedor no e-mail.',
      nextStep:
        'Envie a nota fiscal ou informe à equipe financeira o identificador fiscal (CNPJ ou CPF, se for brasileiro) e o país do fornecedor.',
    },
    finance: {
      message:
        'O identificador fiscal do fornecedor não está no e-mail. O remetente não foi consultado: a resposta já tinha sido enviada ou a caixa de entrada não responde automaticamente.',
      nextStep: 'Peça o identificador fiscal e o país ao fornecedor e envie a conta manualmente.',
    },
  },
  EMAIL_INTAKE_NEEDS_SENDER_INFO: {
    sender: {
      message: 'Faltam o documento ou dados da cobrança para continuar.',
      nextStep: 'Envie a nota fiscal, o boleto ou a fatura à equipe financeira.',
    },
    finance: {
      message:
        'Faltam no e-mail o documento ou dados da cobrança. O remetente não foi consultado: a resposta já tinha sido enviada ou a caixa de entrada não responde automaticamente.',
      nextStep: 'Peça ao fornecedor o que falta e envie a conta manualmente.',
    },
  },
  EMAIL_INTAKE_ENTITY_UNRESOLVED: {
    sender: {
      message: 'Recebemos o documento; a equipe financeira vai indicar de qual empresa ele é.',
      nextStep: 'Nenhuma ação necessária.',
    },
    finance: {
      message: 'Esta caixa atende várias empresas, e o e-mail não diz de qual empresa é a conta.',
      nextStep: 'Escolha a empresa. O e-mail será processado de novo para ela.',
    },
  },
};

/**
 * English finance copy per reason code, next to the pt-BR catalog so a new code cannot ship
 * without it (the web and API/MCP consumers derive both locales from here). Finance audience
 * only: the sender reply (S2) is pt-BR.
 */
export const EMAIL_INTAKE_REASON_COPY_EN: Record<
  EmailIntakeReasonCode,
  { finance: EmailIntakeReasonCopy }
> = {
  EMAIL_INTAKE_BILL_CREATED: {
    finance: {
      message: 'A bill was created from the document.',
      nextStep: 'No action needed.',
    },
  },
  EMAIL_INTAKE_REIMBURSEMENT_CREATED: {
    finance: {
      message: 'A reimbursement request was opened from the document.',
      nextStep: 'Follow the reimbursement approval in the system.',
    },
  },
  EMAIL_INTAKE_DUPLICATE_DOCUMENT: {
    finance: {
      message: 'This invoice was sent before and already has a bill.',
      nextStep: 'No action needed; the existing bill is linked.',
    },
  },
  EMAIL_INTAKE_NOT_PAYABLE_DOCUMENT: {
    finance: {
      message: 'The file was read as a document that is not paid (receipt, tax guide, signature).',
      nextStep: 'Check the file; if it is payable, create the bill manually.',
    },
  },
  EMAIL_INTAKE_LOW_CONFIDENCE: {
    finance: {
      message: 'The file could not be read reliably.',
      nextStep: 'Open the file and, if it is payable, create the bill manually.',
    },
  },
  EMAIL_INTAKE_VENDOR_TAX_ID_UNREADABLE: {
    finance: {
      message: "The vendor's CNPJ/CPF was not found in the document.",
      nextStep: 'Check the document and create the bill manually, or wait for it to be sent again.',
    },
  },
  EMAIL_INTAKE_MANUAL_BILL_REQUIRED: {
    finance: {
      message:
        'The document was read, but the vendor or the company could not be identified automatically.',
      nextStep: 'Create the bill manually from the document in the system.',
    },
  },
  EMAIL_INTAKE_LINKED_TO_EXISTING_BILL: {
    finance: {
      message: 'The document was attached to a bill that already existed.',
      nextStep: 'No action needed; the existing bill is linked.',
    },
  },
  EMAIL_INTAKE_PROCESSING_ERROR: {
    finance: {
      message: 'A technical error happened while processing the e-mail.',
      nextStep: 'Open the attachments and, if anything is payable, create the bill manually.',
    },
  },
  EMAIL_INTAKE_NO_ATTACHMENT: {
    finance: {
      message: 'The e-mail has no usable attachment and the text was not enough to create a bill.',
      nextStep: 'Check the e-mail; if anything is payable, ask the sender for the document.',
    },
  },
  EMAIL_INTAKE_PASSWORD_REQUIRED: {
    finance: {
      message: 'A PDF is password protected; the sender was asked for the password.',
      nextStep: "Wait for the sender's answer.",
    },
  },
  EMAIL_INTAKE_AWAITING_INFO: {
    finance: {
      message: 'Information is missing; the sender was asked for it.',
      nextStep: "Wait for the sender's answer.",
    },
  },
  EMAIL_INTAKE_SPAM: {
    finance: {
      message: 'The e-mail was classified as spam and discarded.',
      nextStep: 'No action needed.',
    },
  },
  EMAIL_INTAKE_INTEGRATION_CHANNEL: {
    finance: {
      message: 'The request received through the integration (Pipefy) did not create a bill.',
      nextStep: 'Check the card in the integration or create the bill manually.',
    },
  },
  EMAIL_INTAKE_SELF_ISSUED_DOCUMENT: {
    finance: {
      message:
        "The invoice was issued by one of the group's companies (the issuer CNPJ is ours); no bill was created.",
      nextStep:
        'If it is a sale, record it in accounts receivable; if it really is payable, create the bill manually.',
    },
  },
  EMAIL_INTAKE_NEEDS_SENDER_PASSWORD: {
    finance: {
      message:
        'One of the PDFs is password-protected. The sender was not asked: they had already been answered, or the inbox does not reply automatically.',
      nextStep: 'Ask the vendor for the password and send the bill manually.',
    },
  },
  EMAIL_INTAKE_NEEDS_SENDER_TAX_ID: {
    finance: {
      message:
        "The vendor's tax ID is not in the e-mail. The sender was not asked: they had already been answered, or the inbox does not reply automatically.",
      nextStep: 'Ask the vendor for its tax ID and country and send the bill manually.',
    },
  },
  EMAIL_INTAKE_NEEDS_SENDER_INFO: {
    finance: {
      message:
        'The document or billing data is missing from the e-mail. The sender was not asked: they had already been answered, or the inbox does not reply automatically.',
      nextStep: 'Ask the vendor for what is missing and send the bill manually.',
    },
  },
  EMAIL_INTAKE_ENTITY_UNRESOLVED: {
    finance: {
      message:
        'This inbox serves several companies, and the e-mail does not say which one the bill is for.',
      nextStep: 'Choose the company. The e-mail will be processed again for it.',
    },
  },
};

/**
 * Short per-file label (finance audience, sender-independent) for the file chips of the no-bill
 * card (S5): one line under each file when the files of an e-mail ended for different reasons.
 */
export const EMAIL_INTAKE_REASON_FILE_LABELS: Record<
  EmailIntakeReasonCode,
  { ptBr: string; en: string }
> = {
  EMAIL_INTAKE_BILL_CREATED: { ptBr: 'Conta criada', en: 'Bill created' },
  EMAIL_INTAKE_REIMBURSEMENT_CREATED: { ptBr: 'Reembolso aberto', en: 'Reimbursement opened' },
  EMAIL_INTAKE_DUPLICATE_DOCUMENT: { ptBr: 'Nota já lançada', en: 'Invoice already billed' },
  EMAIL_INTAKE_NOT_PAYABLE_DOCUMENT: {
    ptBr: 'Não é documento a pagar',
    en: 'Not a payable document',
  },
  EMAIL_INTAKE_LOW_CONFIDENCE: { ptBr: 'Leitura incerta', en: 'Unreliable reading' },
  EMAIL_INTAKE_VENDOR_TAX_ID_UNREADABLE: {
    ptBr: 'CNPJ do fornecedor ilegível',
    en: "Vendor's CNPJ unreadable",
  },
  EMAIL_INTAKE_MANUAL_BILL_REQUIRED: {
    ptBr: 'Fornecedor ou empresa não identificados',
    en: 'Vendor or company not identified',
  },
  EMAIL_INTAKE_LINKED_TO_EXISTING_BILL: {
    ptBr: 'Vinculado a uma conta existente',
    en: 'Linked to an existing bill',
  },
  EMAIL_INTAKE_PROCESSING_ERROR: { ptBr: 'Erro ao processar', en: 'Processing error' },
  EMAIL_INTAKE_NO_ATTACHMENT: { ptBr: 'Sem anexo utilizável', en: 'No usable attachment' },
  EMAIL_INTAKE_PASSWORD_REQUIRED: { ptBr: 'PDF com senha', en: 'Password-protected PDF' },
  EMAIL_INTAKE_AWAITING_INFO: { ptBr: 'Aguardando informações', en: 'Awaiting information' },
  EMAIL_INTAKE_SPAM: { ptBr: 'Spam', en: 'Spam' },
  EMAIL_INTAKE_INTEGRATION_CHANNEL: { ptBr: 'Sem conta (Pipefy)', en: 'No bill (Pipefy)' },
  EMAIL_INTAKE_SELF_ISSUED_DOCUMENT: {
    ptBr: 'Nota emitida pelo grupo',
    en: 'Invoice issued by the group',
  },
  EMAIL_INTAKE_NEEDS_SENDER_PASSWORD: {
    ptBr: 'Falta a senha do PDF',
    en: 'PDF password missing',
  },
  EMAIL_INTAKE_NEEDS_SENDER_TAX_ID: {
    ptBr: 'Falta o identificador fiscal do fornecedor',
    en: "Vendor's tax ID missing",
  },
  EMAIL_INTAKE_NEEDS_SENDER_INFO: {
    ptBr: 'Faltam dados do remetente',
    en: 'Sender data missing',
  },
  EMAIL_INTAKE_ENTITY_UNRESOLVED: {
    ptBr: 'Empresa não identificada',
    en: 'Company not identified',
  },
};
